import type React from 'react';
import { useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { Alert, Anchor, Box, Button, Heading, Image, Paragraph, TextButton } from '@zillow/constellation-10';
import Page from '@common/components/Page';
import { PROPERTY_STATUS } from '@rental-manager/constants/OnboardingConstants';
import userPersonas from '@common/constants/userPersonas';
import StringConstants from '@common/constants/StringConstants';
import REQUEST_STATUS from '@common/constants/requestStatus';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import Modal from '@common/components/ConstellationV10Modal';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  baseUrl: string;
  propertyStatus: string;
  showSetupNofication: () => void;
  onNext: (collectMoveInCosts: boolean) => void;
  collectMoveInCosts: boolean;
  getLandlordStatusRequestStatus: string;
  getLandlordStatusMessageCode: string;
  currentProperty?: ZrmClickstreamListing;
  user?: ZrmClickstreamUser;
}

// Shared Foundations spot illustration served from the ZG DAM.
// URL pattern: [asset-name]-[light|dark]_svg_Original.svg
const ONLINE_PAYMENT_SPOT_ILLUSTRATION =
  'https://delivery.digitallibrary.zillowgroup.com/public/online-payment-light_svg_Original.svg';

const OnboardingLandingPage: React.FC<Props> = ({
  baseUrl,
  propertyStatus = PROPERTY_STATUS.NOT_STARTED,
  showSetupNofication,
  onNext,
  collectMoveInCosts = false,
  getLandlordStatusRequestStatus,
  getLandlordStatusMessageCode,
  currentProperty,
  user,
}) => {
  useEffect(() => {
    const isBaseUrl = matchPath(window.location.pathname, {
      path: baseUrl,
      exact: true,
      strict: false,
    });
    if (
      propertyStatus !== PROPERTY_STATUS.NOT_STARTED &&
      propertyStatus !== PROPERTY_STATUS.TENANT_INFO_SET &&
      isBaseUrl
    ) {
      showSetupNofication();
    }
  }, [baseUrl, propertyStatus, showSetupNofication]);

  const gotoNextPage = (): void => {
    if (currentProperty && user) {
      IreneOnboardingCSEvents.emitStartCollectingPaymentsEvent({ currentProperty, user });
    }
    onNext(collectMoveInCosts);
  };

  const trackLearnMoreClick = (): void => {
    if (currentProperty && user) {
      IreneOnboardingCSEvents.emitLearnMoreLinkEvent({ currentProperty, user });
    }
  };

  const renderCTA = (startButtonCopy: Record<string, string>): React.ReactNode => {
    return (
      <Box>
        <Button emphasis="filled" fluid size="md" onClick={gotoNextPage}>
          {collectMoveInCosts ? startButtonCopy.mic : startButtonCopy.default}
        </Button>
      </Box>
    );
  };

  const shouldShowLeaseRetrievalFailedModal =
    getLandlordStatusRequestStatus === REQUEST_STATUS.FAIL &&
    getLandlordStatusMessageCode === ResponseMessageCode.LEASE_RETRIEVAL_FAILED;

  const utmCampaign = 'utm_campaign=payment_tab_learn_more';
  const marketingLink = `${StringConstants.LINKS.MARKETING_PAGE.ZRM_PAYMENTS}?${utmCampaign}`;
  const startButtonCopy = { default: 'Start collecting payments', mic: 'Collect move-in costs' };
  return (
    <Page persona={userPersonas.LANDLORD} dataAutoTestId="landlord-onboard-landing-page">
      <Box
        css={{
          marginBlock: 'layout.looser',
          marginInline: 'auto',
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'tighter',
          maxWidth: { lg: '60%', md: '80%', sm: '100%' },
        }}
      >
        <Box>
          <Image src={ONLINE_PAYMENT_SPOT_ILLUSTRATION} alt="" width={120} height={120} css={{ display: 'block' }} />
          <Heading level={1} textStyle="heading-xs" css={{ marginBlockStart: 'default' }}>
            Say hello to online payments
          </Heading>
        </Box>

        <Box css={{ marginBlockStart: 'default' }}>
          <Paragraph css={{ color: 'text.subtle' }}>
            You get a free, secure way to collect rent and move-in costs right into your bank account. Your tenant gets
            a free way to pay and more flexibility. You both get freedom from checks.
          </Paragraph>
          <TextButton asChild css={{ justifySelf: 'start', marginBlockStart: 'tighter' }}>
            <a onClick={trackLearnMoreClick} href={marketingLink} target="_blank" rel="noopener noreferrer">
              Learn more
            </a>
          </TextButton>
        </Box>
        <Box css={{ marginBlockStart: 'default' }}>{renderCTA(startButtonCopy)}</Box>
        <Box>
          <Paragraph css={{ color: 'text.subtle' }} textStyle="fineprint">
            By continuing, you agree to comply with the{' '}
            <Anchor target="_blank" rel="noopener noreferrer" href="https://www.zillow.com/corp/Terms.htm">
              Terms of Use
            </Anchor>
            ,{' '}
            <Anchor
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms"
            >
              Rental User Terms
            </Anchor>
            , and our third-party payment processor’s{' '}
            <Anchor target="_blank" rel="noopener noreferrer" href="https://stripe.com/us/connect-account/legal">
              Payment Terms
            </Anchor>{' '}
            and{' '}
            <Anchor target="_blank" rel="noopener noreferrer" href="https://plaid.com/legal">
              Bank Authorization Terms
            </Anchor>
            .
          </Paragraph>
        </Box>
      </Box>
      {shouldShowLeaseRetrievalFailedModal && (
        <Modal
          persona={userPersonas.LANDLORD}
          header="A problem occurred"
          body={
            <Alert tone="critical">
              We aren’t currently able to access your leases, which is required for setting up payments. Wait a few
              minutes and try again.
            </Alert>
          }
          footer={<DebouncedButton onClick={() => window.location.reload()}>Try again</DebouncedButton>}
        />
      )}
    </Page>
  );
};

export default OnboardingLandingPage;
